import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { FileDropZone } from '../../common/FileDropZone';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import type { SidebarFile } from '../../../interfaces/sidebar';

interface FileExplorerProps {
  /** All files in the workspace - status field handles uploading/parsing/success/error states */
  files: SidebarFile[];
  
  /** Callback for file upload */
  onFileUpload?: (file: File, options?: { overwriteDuplicates?: boolean }) => Promise<void>;
  
  /** Callback when a file is deleted - receives the file ID */
  onFileDelete?: (fileId: string) => void;
  
  /** Callback when all files should be deleted */
  onDeleteAll?: () => void;
  
  /** Maximum file size for uploads (MB) */
  maxFileSize?: number;
  
  /** Accepted file types for uploads */
  acceptedTypes?: string[];
}

/**
 * File management component for uploads, file status, and file operations
 * 
 * Extracted from FileTab to provide reusable file management functionality
 * across different contexts. Handles:
 * - File uploads via drag-and-drop or browse
 * - File status display (uploading, parsing, success, error)
 * - File deletion
 * - Progress tracking
 * 
 * Key Design Principles:
 * 1. Uses clean SidebarFile interface - no direct parsedData access
 * 2. Separates file management from curve exploration
 * 3. Provides comprehensive file operation callbacks
 */
export function FileExplorer({
  files,
  onFileUpload,
  onFileDelete,
  onDeleteAll,
  maxFileSize = 100,
  acceptedTypes = ['.csv', '.xlsx', '.las']
}: FileExplorerProps) {
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<SidebarFile | null>(null);
  
  const handleFileUpload = async (uploadedFiles: File[]) => {
    if (!onFileUpload) return;
    
    // Process all uploads in parallel instead of serially
    const uploadPromises = uploadedFiles.map(async (file) => {
      try {
        await onFileUpload(file);
        return { success: true, file: file.name };
      } catch (error) {
        console.error('File upload failed:', error);
        return { success: false, file: file.name, error };
      }
    });
    
    // Wait for all uploads to complete in parallel
    await Promise.allSettled(uploadPromises);
  };

  const getFileProgress = (file: SidebarFile) => {
    return file.progress || 0;
  };

  const getFileCurveCount = (file: SidebarFile) => {
    return file.curves?.length || 0;
  };

  const totalFiles = files.length;
  const successfulFiles = files.filter(f => f.status === 'success').length;
  const errorFiles = files.filter(f => f.status === 'error').length;
  const uploadingFiles = files.filter(f => f.status === 'uploading');
  
  // Calculate deletable files (success or error status, not uploading/parsing)
  const deletableFiles = files.filter(f => f.status === 'success' || f.status === 'error');
  const deletableCount = deletableFiles.length;

  // Delete All handlers
  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    if (onDeleteAll) {
      onDeleteAll();
    }
    setDeleteAllDialogOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  // Individual file delete handlers
  const handleFileDeleteClick = (file: SidebarFile) => {
    setFileToDelete(file);
    setDeleteFileDialogOpen(true);
  };

  const handleFileDeleteConfirm = () => {
    if (onFileDelete && fileToDelete) {
      onFileDelete(fileToDelete.id);
    }
    setDeleteFileDialogOpen(false);
    setFileToDelete(null);
  };

  const handleFileDeleteCancel = () => {
    setDeleteFileDialogOpen(false);
    setFileToDelete(null);
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      gap: 2,
      p: 2
    }}>
      
      {/* Header Stats */}
      <Box>
        <Typography variant="h6" gutterBottom>
          File Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip 
            label={`${totalFiles} files`} 
            size="small" 
            variant="outlined"
          />
          {successfulFiles > 0 && (
            <Chip 
              label={`${successfulFiles} loaded`} 
              size="small" 
              color="success"
              variant="outlined"
            />
          )}
          {errorFiles > 0 && (
            <Chip 
              label={`${errorFiles} errors`} 
              size="small" 
              color="error"
              variant="outlined"
            />
          )}
          {onDeleteAll && deletableCount > 0 && (
            <Tooltip title={`Delete all ${deletableCount} files`}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleDeleteAllClick}
                sx={{
                  minHeight: 24,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'error.contrastText'
                  }
                }}
              >
                Delete All
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Upload Section */}
      {onFileUpload && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Upload Files
          </Typography>
          <FileDropZone
            onFileUpload={handleFileUpload}
            acceptedTypes={acceptedTypes}
            maxFileSize={maxFileSize}
            isUploading={uploadingFiles.length > 0}
          />
        </Box>
      )}

      {/* File List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom>
          Workspace Files
        </Typography>
        
        {totalFiles === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No files in workspace. Upload files above to get started.
          </Alert>
        ) : (
          <List dense>
            {/* All files - rendered differently based on status */}
            {files.map((file) => {
              const progress = getFileProgress(file);
              const curveCount = getFileCurveCount(file);
              
              // Uploading files
              if (file.status === 'uploading') {
                return (
                  <ListItem key={`uploading-${file.id}`}>
                    <ListItemIcon>
                      <UploadIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Uploading... {progress}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={progress} 
                            sx={{ mt: 0.5, height: 3 }}
                          />
                          {file.error && (
                            <Typography variant="caption" color="error.main" display="block">
                              {file.error.message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              }

              // Parsing/fetching files
              if (file.status === 'fetching' || file.status === 'parsing') {
                return (
                  <ListItem key={`processing-${file.id}`}>
                    <ListItemIcon>
                      <RefreshIcon color="primary" className="spin" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      secondary={file.status === 'fetching' ? 'Fetching file...' : 'Parsing file...'}
                    />
                  </ListItem>
                );
              }

              // Success or error files
              return (
                <ListItem key={file.id}>
                  <ListItemIcon>
                    {file.status === 'success' ? (
                      <SuccessIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.filename}
                    secondary={
                      file.status === 'success' 
                        ? `${curveCount} curves available`
                        : file.error?.message || 'Unknown error'
                    }
                  />
                  {onFileDelete && (
                    <ListItemSecondaryAction>
                      <Tooltip title="Delete file">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleFileDeleteClick(file)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        aria-labelledby="delete-all-dialog-title"
        aria-describedby="delete-all-dialog-description"
      >
        <DialogTitle id="delete-all-dialog-title">
          Delete All Files
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-all-dialog-description">
            Are you sure you want to delete all {deletableCount} files? This action cannot be undone.
          </DialogContentText>
          {deletableCount > 1 && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Files to be deleted: {successfulFiles} successful, {errorFiles} with errors
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAllCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAllConfirm} 
            color="error" 
            variant="contained"
            startIcon={<DeleteSweepIcon />}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual File Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteFileDialogOpen}
        title="Delete File"
        message="Are you sure you want to delete this file? This action cannot be undone."
        fileName={fileToDelete?.filename}
        onConfirm={handleFileDeleteConfirm}
        onCancel={handleFileDeleteCancel}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
} 